import { Component, OnInit, inject } from '@angular/core';
import {MatDialogRef} from '@angular/material/dialog';
import {User} from "../../../model/user/user.model";
import {InputTextComponent} from "../../form-parts/form-items/input-text/input-text.component";
import {formatPhone} from "../../utils.static";
import {PhoneRegionCodeEnum} from "../../../model/enums/phone-region-code.enum";
import {UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {debounceTime, Subject, takeUntil} from 'rxjs';
import {Destroyed} from '../../directives/destroyed.directive';
import {FormatService} from "../../../services/format.service";
import {UserWebservice} from "../../../services/webservices/user.webservice";
import {FormSelectItem} from "../../../model/form/select-item.model";
import {SelectComponent} from "../../form-parts/form-items/select/select.component";
import {UserEmailAzureDTO, UserPhoneAzureDTO} from "../../../model/dto/user-email-azure.dto";
import { MsalService } from '@azure/msal-angular';
import {ToastService} from "../../../services/toast.service";
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner";
import {NgClass} from "@angular/common";


@Component({
    selector: 'app-dialog-modification-profile',
    imports: [
        InputTextComponent,
        SelectComponent,
        MatProgressSpinnerModule,
        NgClass
    ],
    templateUrl: './dialog-modification-profile.component.html',
    styleUrl: './dialog-modification-profile.component.scss'
})
export class DialogModificationProfileComponent extends Destroyed implements OnInit{
  private readonly dialogRef = inject<MatDialogRef<DialogModificationProfileComponent>>(MatDialogRef);
  private readonly fb = inject(UntypedFormBuilder);
  private readonly formatService = inject(FormatService);
  private readonly userWebservice = inject(UserWebservice);
  private readonly msalService = inject(MsalService);
  private readonly toastService = inject(ToastService);


  currentUser: User;
  phoneNumber: string;
  newPhoneNumber: string;
  mailRegex = new RegExp('^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\\.[a-zA-Z0-9-.]+$');
  phoneRegex = new RegExp('(^0[6-7][0-9]{8}$)|(^[6-7][0-9]{8}$)');

  phoneRegionCodeList: string[];
  phoneRegionCodeForSelect: FormSelectItem[] = [];

  phoneFormControl: UntypedFormGroup;
  emailFormControl: UntypedFormGroup;

  isPhoneCorrect = false;
  isEmailCorrect = false;

  mailUserInfos: UserEmailAzureDTO;
  phoneUserInfos: UserPhoneAzureDTO;

  cancelPreviousRequest: Subject<void> = new Subject<void>();

  isEmailLoading = false;
  isPhoneLoading = false;
  showForceDisconnect: boolean = false;

  constructor() {
    super();
    this.currentUser = this.userWebservice.currentUser;
  }

  get fPhone() {
    return this.phoneFormControl.controls;
  }

  get fEmail() {
    return this.emailFormControl.controls;
  }

  ngOnInit() {
    if (this.currentUser?.phone) {
      const regionCode = this.currentUser.phone.slice(0, 3);
      this.phoneNumber = formatPhone(
        PhoneRegionCodeEnum[regionCode],
        this.currentUser.phone.slice(3)
      );
    }


    this.phoneFormControl = this.fb.group({
      phone: [null, Validators.required],
      phoneCode: [null, Validators.required]
    });

    this.emailFormControl = this.fb.group({
      email: [null, Validators.required]
    });

    this.handleEmailFormChanges();
    this.handlePhoneFormChanges();

    this.initPhoneCodeSelector();
    this.initDefaultValueInputs();

    this.mailUserInfos = new UserEmailAzureDTO();
    this.phoneUserInfos = new UserPhoneAzureDTO();
    this.mailUserInfos.azureId = this.msalService.instance.getAllAccounts()[0].localAccountId;
    this.phoneUserInfos.azureId = this.msalService.instance.getAllAccounts()[0].localAccountId;
  }

  initDefaultValueInputs() {
    this.phoneFormControl.controls.phone.setValue(this.phoneNumber);
    this.emailFormControl.controls.email.setValue(this.currentUser.email);
  }

  initPhoneCodeSelector() {
    this.phoneRegionCodeList = [];
    this.phoneRegionCodeForSelect = [];

    const PhoneRegionCodeEnumLength = Object.keys(PhoneRegionCodeEnum).length / 2;

    for (let i = 0; i < PhoneRegionCodeEnumLength; i++) {
      this.phoneRegionCodeList[i] = PhoneRegionCodeEnum[i];
    }

    for (const key of this.phoneRegionCodeList.keys()) { // Création du tableau pour le select, ici <id, name>
      this.phoneRegionCodeForSelect.push({id: Number(key), name: this.phoneRegionCodeList[key]});
    }

    this.phoneFormControl.controls.phoneCode.setValue(0);
  }

  tronqPhoneNumber(value) {
    let telTronq = '';
    if (value.length > 8 && value.length <= 10) {
      telTronq = value.length === 10 ? value.slice(1) : value;
      this.newPhoneNumber = `${PhoneRegionCodeEnum[this.phoneFormControl.controls.phoneCode.value]}${telTronq}`;
    }
  }

  private handlePhoneFormChanges() {
    this.phoneFormControl.controls.phone.valueChanges.pipe(takeUntil(this.destroyed), debounceTime(200))
      .subscribe(value => {
        if (value !== '' && value && value) {
          if (this.phoneRegex.test(value) && value !== this.phoneNumber) {
            this.tronqPhoneNumber(value);

            this.cancelPreviousRequest.next();
            this.userWebservice.postCheckPhoneExist(this.newPhoneNumber, this.currentUser.id)
              .pipe(takeUntil(this.cancelPreviousRequest), takeUntil(this.destroyed))
              .subscribe((alreadyExists: boolean) => {
                this.isPhoneCorrect = !alreadyExists;
              });
            this.isPhoneCorrect = true;
          } else {
            this.isPhoneCorrect = false;
            this.phoneFormControl.get('phone').setErrors({'incorrect': true});
          }
        }
      });
  }

  submitMail() {
    if (this.isEmailCorrect) {
      this.mailUserInfos.mail = this.emailFormControl.controls.email.value;
      this.isEmailCorrect = false;
      this.isEmailLoading = true;
      this.userWebservice.updateEmailRequest(this.mailUserInfos)
        .pipe(takeUntil(this.destroyed))
        .subscribe(() => {
          this.toastService.success('L\'adresse mail a bien été modifiée', 'Succès');
          this.isEmailLoading = false;
          this.showForceDisconnect = true;
        });
    } else {
      this.toastService.warn('L\'adresse mail ne peut pas être modifiée', 'Attention');
    }
  }

  submitPhone() {
    if (this.isPhoneCorrect) {
      this.phoneUserInfos.phone = this.newPhoneNumber;
      this.isPhoneCorrect = false;
      this.isPhoneLoading = true;
      this.userWebservice.updatePhoneRequest(this.phoneUserInfos)
        .pipe(takeUntil(this.destroyed))
        .subscribe(() => {
          this.toastService.success('Le téléphone a bien été modifié', 'Succès');
          this.isPhoneLoading = false;
        });
    }else {
      this.toastService.warn('Le téléphone ne peut pas être modifié', 'Attention');
    }
  }


  private handleEmailFormChanges() {
    this.emailFormControl.controls.email.valueChanges.pipe(takeUntil(this.destroyed), debounceTime(200))
      .subscribe(value => {
        if (value && value !=='' && value !== undefined) {
          const mail  = this.formatService.trim(value);

          if (this.mailRegex.test(mail) && mail !== this.currentUser.email) {
            this.cancelPreviousRequest.next();
            this.userWebservice.postCheckEmailExist(mail, this.currentUser.id)
              .pipe(takeUntil(this.cancelPreviousRequest), takeUntil(this.destroyed))
              .subscribe((alreadyExists: boolean) => {
                this.isEmailCorrect = !alreadyExists;
              });
          } else {
            this.isEmailCorrect = false;
          }
        }
      });
  }

  close() {
    this.dialogRef.close();
  }

  disconnect() {
    this.msalService.logoutRedirect();
  }
}
